import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Export modal for Client Review results.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - resultId: string (for single result export)
 *  - jobId: string (for full job export)
 *  - scope: 'result' | 'job'
 *  - title: string (display title)
 *  - token: string (auth token)
 */
export default function ClientReviewExportModal({ open, onClose, resultId, jobId, scope, title, token }) {
  const [format, setFormat] = useState('pdf');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    setDownloading(true);
    setError(null);

    try {
      let url;
      if (scope === 'result') {
        url = `${API_BASE_URL}/client-reviews/results/${resultId}/export/${format}`;
      } else {
        url = `${API_BASE_URL}/client-reviews/jobs/${jobId}/export/${format}`;
      }

      const responseType = format === 'pdf' ? 'blob' : format === 'markdown' ? 'text' : 'json';

      const response = await axios.get(url, {
        headers: { 'x-auth-token': token },
        responseType
      });

      let blob;
      let extension;

      if (format === 'pdf') {
        blob = new Blob([response.data], { type: 'application/pdf' });
        extension = 'pdf';
      } else if (format === 'markdown') {
        blob = new Blob([response.data], { type: 'text/markdown' });
        extension = 'md';
      } else {
        const jsonStr = typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data, null, 2);
        blob = new Blob([jsonStr], { type: 'application/json' });
        extension = 'json';
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const prefix = scope === 'result' ? `client-review-${resultId}` : `client-review-job-${jobId}`;
      a.download = `${prefix}-${Date.now()}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);

      onClose();
    } catch (err) {
      console.error('Error exporting client review:', err);
      setError('Failed to export. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title || 'Export Review'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {scope === 'result'
            ? 'Export this client review result in your preferred format.'
            : 'Export all results and the cross-client summary.'}
        </Typography>

        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Format</InputLabel>
          <Select
            value={format}
            label="Format"
            onChange={(e) => setFormat(e.target.value)}
          >
            <MenuItem value="pdf">PDF Document</MenuItem>
            <MenuItem value="markdown">Markdown</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={downloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={downloading}
          startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          {downloading ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
